import { supabase } from "@/integrations/supabase/client";

export interface UploadResult {
  url: string;
  error?: string;
}

export const uploadImage = async (file: File, bucket: "product-images" | "genie-images", path?: string): Promise<UploadResult> => {
  try {
    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      return { url: "", error: "Invalid file type. Please upload a JPEG, PNG, WebP, or GIF image." };
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { url: "", error: "File too large. Please upload an image smaller than 5MB." };
    }

    // Generate unique filename
    const fileExt = file.name.split(".").pop();
    const fileName = `${Math.random().toString(36).substring(2, 9)}-${Date.now()}.${fileExt}`;
    const filePath = path ? `${path}/${fileName}` : fileName;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage.from(bucket).upload(filePath, file, {
      cacheControl: "3600",
      upsert: false,
    });

    if (error) {
      console.error("Upload error:", error);
      return { url: "", error: "Failed to upload image. Please try again." };
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from(bucket).getPublicUrl(data.path);

    return { url: publicUrl };
  } catch (error) {
    console.error("Upload error:", error);
    return { url: "", error: "An unexpected error occurred. Please try again." };
  }
};

export const deleteImage = async (url: string, bucket: "product-images" | "genie-images"): Promise<boolean> => {
  try {
    // If URL is not from our Supabase storage bucket, there's nothing to delete server-side.
    // Treat as success so the caller can clear the reference (helps with default/public images).
    const marker = `/storage/v1/object/public/${bucket}/`;
    if (!url.includes(marker)) {
      return true;
    }

    // Extract path from URL
    const urlParts = url.split(marker);
    if (urlParts.length !== 2) {
      console.error("Invalid URL format");
      return false;
    }

    const path = urlParts[1];

    const { error } = await supabase.storage.from(bucket).remove([path]);

    if (error) {
      console.error("Delete error:", error);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Delete error:", error);
    return false;
  }
};
